import { FunctionComponent, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import VerifiedIcon from '@mui/icons-material/Verified';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styles from './SpecialtyDetail.module.css';

interface Provider {
  npi: string;
  name: string;
  specialty: string;
  subspecialties: string[];
  distance: string;
  rating: number;
  reviewCount: number;
  nextAvailable: string;
  acceptsInsurance: boolean;
  estimatedCost: number;
  savings: number;
  hospital: string;
  languages: string[];
  gender: string;
  yearsExperience: number;
  imageUrl?: string;
}

interface Procedure {
  cpt: string;
  name: string;
  category: string;
  averagePrice: number;
  priceRange: { min: number; max: number };
  providers: number;
  featured?: boolean;
}

interface SpecialtyInfo {
  code: string;
  name: string;
  description: string;
  commonProcedures: string[];
  averageCost: {
    consultation: { min: number; max: number };
    procedure: { min: number; max: number };
  };
  totalProviders: number;
  providers: Provider[];
  procedures: Procedure[];
}

const SpecialtyDetail: FunctionComponent = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [specialtyInfo, setSpecialtyInfo] = useState<SpecialtyInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'distance' | 'rating' | 'cost' | 'availability'>('distance');
  const [showFilters, setShowFilters] = useState(false);
  const handleViewAllProviders = () => {
    navigate(`/search?q=${encodeURIComponent(specialtyInfo?.name || '')}&type=providers&taxonomy=${code}`);
  };
  
  const handleViewAllProcedures = () => {
    navigate(`/search?q=${encodeURIComponent(specialtyInfo?.name || '')}&type=procedures&specialty=${code}`);
  };

  useEffect(() => {
    // Mock data for demonstration
    const mockSpecialtyData: SpecialtyInfo = {
      code: code || '207Q00000X',
      name: 'Family Medicine',
      description: 'Family medicine physicians provide comprehensive healthcare for people of all ages. They diagnose and treat a wide variety of ailments in patients ranging from newborns to elderly adults.',
      commonProcedures: [
        'Annual Physical Exam',
        'Sick Visits',
        'Chronic Disease Management',
        'Preventive Care',
        'Minor Procedures',
        'Health Screenings'
      ],
      averageCost: {
        consultation: { min: 150, max: 300 },
        procedure: { min: 200, max: 500 }
      },
      totalProviders: 47,
      providers: [
        {
          npi: '1234567890',
          name: 'Dr. Sarah Johnson',
          specialty: 'Family Medicine',
          subspecialties: ['Sports Medicine', 'Geriatrics'],
          distance: '0.8 mi',
          rating: 4.8,
          reviewCount: 245,
          nextAvailable: '2024-01-10',
          acceptsInsurance: true,
          estimatedCost: 45,
          savings: 120,
          hospital: 'St. Mary\'s Medical Center',
          languages: ['English', 'Spanish'],
          gender: 'Female',
          yearsExperience: 15
        },
        {
          npi: '0987654321',
          name: 'Dr. Michael Chen',
          specialty: 'Family Medicine',
          subspecialties: ['Pediatrics'],
          distance: '1.2 mi',
          rating: 4.9,
          reviewCount: 189,
          nextAvailable: '2024-01-12',
          acceptsInsurance: true,
          estimatedCost: 50,
          savings: 115,
          hospital: 'City General Hospital',
          languages: ['English', 'Mandarin', 'Cantonese'],
          gender: 'Male',
          yearsExperience: 12
        },
        {
          npi: '1122334455',
          name: 'Dr. Emily Rodriguez',
          specialty: 'Family Medicine',
          subspecialties: ['Women\'s Health'],
          distance: '2.5 mi',
          rating: 4.7,
          reviewCount: 156,
          nextAvailable: '2024-01-08',
          acceptsInsurance: true,
          estimatedCost: 40,
          savings: 125,
          hospital: 'Riverside Medical Group',
          languages: ['English', 'Spanish', 'Portuguese'],
          gender: 'Female',
          yearsExperience: 8
        }
      ],
      procedures: [
        {
          cpt: '99213',
          name: 'Office Visit - Established Patient',
          category: 'Evaluation & Management',
          averagePrice: 150,
          priceRange: { min: 100, max: 250 },
          providers: 45,
          featured: true
        },
        {
          cpt: '99385',
          name: 'Annual Physical Exam',
          category: 'Preventive Care',
          averagePrice: 200,
          priceRange: { min: 150, max: 300 },
          providers: 42
        },
        {
          cpt: '81001',
          name: 'Urinalysis',
          category: 'Laboratory',
          averagePrice: 25,
          priceRange: { min: 15, max: 40 },
          providers: 38
        },
        {
          cpt: '90471',
          name: 'Immunization Administration',
          category: 'Preventive Care',
          averagePrice: 30,
          priceRange: { min: 20, max: 45 },
          providers: 40
        }
      ]
    };

    // Simulate API call
    setTimeout(() => {
      setSpecialtyInfo(mockSpecialtyData);
      setIsLoading(false);
    }, 500);
  }, [code]);

  const handleSortChange = (newSort: typeof sortBy) => {
    setSortBy(newSort);
    if (specialtyInfo) {
      const sorted = [...specialtyInfo.providers];
      switch (newSort) {
        case 'distance':
          sorted.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
          break;
        case 'rating':
          sorted.sort((a, b) => b.rating - a.rating);
          break;
        case 'cost':
          sorted.sort((a, b) => a.estimatedCost - b.estimatedCost);
          break;
        case 'availability':
          sorted.sort((a, b) => new Date(a.nextAvailable).getTime() - new Date(b.nextAvailable).getTime());
          break;
      }
      setSpecialtyInfo({ ...specialtyInfo, providers: sorted });
    }
  };

  const renderProcedureCard = (procedure: Procedure) => (
    <div 
      className={styles.procedureCard}
      onClick={() => navigate(`/procedure/${procedure.cpt}`)}
    >
      <div className={styles.procedureHeader}>
        <h4 className={styles.procedureName}>{procedure.name}</h4>
        <span className={styles.procedureCategory}>{procedure.category}</span>
      </div>
      
      <div className={styles.procedureStats}>
        <div className={styles.priceInfo}>
          <span className={styles.averagePrice}>${procedure.averagePrice}</span>
          <span className={styles.priceRange}>
            ${procedure.priceRange.min}-${procedure.priceRange.max}
          </span>
        </div>
        <span className={styles.providerCount}>{procedure.providers} providers</span>
      </div>
      
      {procedure.featured && (
        <span className={styles.featuredBadge}>
          <StarIcon className={styles.badgeIcon} />
          Popular
        </span>
      )}
    </div>
  );

  const renderProviderCard = (provider: Provider) => (
    <div 
      className={styles.providerCard}
      onClick={() => navigate(`/provider/${provider.npi}`)}
    >
      <div className={styles.providerHeader}>
        <div className={styles.providerIcon}>
          <PersonIcon />
        </div>
        <div className={styles.providerInfo}>
          <h3 className={styles.providerName}>{provider.name}</h3>
          <p className={styles.providerMeta}>
            {provider.subspecialties.length > 0 && (
              <span className={styles.subspecialty}>
                {provider.subspecialties[0]}
              </span>
            )}
            <span className={styles.experience}>
              {provider.yearsExperience} years exp.
            </span>
          </p>
          <div className={styles.providerDetails}>
            <div className={styles.rating}>
              <StarIcon className={styles.starIcon} />
              <span>{provider.rating}</span>
              <span className={styles.reviewCount}>({provider.reviewCount})</span>
            </div>
            <span className={styles.separator}>•</span>
            <span className={styles.hospital}>{provider.hospital}</span>
          </div>
        </div>
        {provider.acceptsInsurance && (
          <span className={styles.networkBadge}>
            <CheckCircleIcon className={styles.checkIcon} />
            In-Network
          </span>
        )}
      </div>

      <div className={styles.providerStats}>
        <div className={styles.statItem}>
          <LocationOnIcon className={styles.statIcon} />
          <span>{provider.distance}</span>
        </div>
        <div className={styles.statItem}>
          <AccessTimeIcon className={styles.statIcon} />
          <span>Next: {new Date(provider.nextAvailable).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.languages}>
            {provider.languages.slice(0, 2).join(', ')}
            {provider.languages.length > 2 && ' +'}
          </span>
        </div>
      </div>

      <div className={styles.costSection}>
        <div className={styles.costInfo}>
          <span className={styles.primaryCost}>${provider.estimatedCost}</span>
          <span className={styles.savingsInfo}>
            <TrendingDownIcon className={styles.savingsIcon} />
            Save ${provider.savings}
          </span>
        </div>
        <span className={styles.verifiedPrice}>
          <VerifiedIcon className={styles.verifiedIcon} />
          Verified Price
        </span>
      </div>

      <button 
        className={styles.viewButton}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/provider/${provider.npi}`);
        }}
      >
        View Profile
        <ChevronRightIcon className={styles.chevronIcon} />
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className={styles.specialtyDetail}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading specialty information...</p>
        </div>
      </div>
    );
  }

  if (!specialtyInfo) {
    return null;
  }

  return (
    <div className={styles.specialtyDetail}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <h1 className={styles.headerTitle}>Specialists</h1>
        <button className={styles.filterButton} onClick={() => setShowFilters(true)}>
          <FilterListIcon />
        </button>
      </div>

      <div className={styles.specialtyHeader}>
        <div className={styles.specialtyIcon}>
          <LocalHospitalIcon />
        </div>
        <div className={styles.specialtyInfo}>
          <h2 className={styles.specialtyName}>{specialtyInfo.name}</h2>
          <p className={styles.specialtyCode}>Taxonomy Code: {specialtyInfo.code}</p>
          <p className={styles.providerCount}>{specialtyInfo.totalProviders} providers near you</p>
        </div>
      </div>

      <div className={styles.specialtyDescription}>
        <p>{specialtyInfo.description}</p>
      </div>

      <div className={styles.infoSection}>
        <h3 className={styles.sectionTitle}>Common Services</h3>
        <div className={styles.proceduresList}>
          {specialtyInfo.commonProcedures.map((procedure, index) => (
            <span key={index} className={styles.procedureChip}>
              {procedure}
            </span>
          ))}
        </div>
      </div>

      <div className={styles.infoSection}>
        <h3 className={styles.sectionTitle}>Average Costs</h3>
        <div className={styles.costRanges}>
          <div className={styles.costRange}>
            <span className={styles.costLabel}>Consultation</span>
            <span className={styles.costValue}>
              ${specialtyInfo.averageCost.consultation.min} - ${specialtyInfo.averageCost.consultation.max}
            </span>
          </div>
          <div className={styles.costRange}>
            <span className={styles.costLabel}>Procedures</span>
            <span className={styles.costValue}>
              ${specialtyInfo.averageCost.procedure.min} - ${specialtyInfo.averageCost.procedure.max}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.sortingBar}>
        <span className={styles.sortLabel}>Sort by:</span>
        <div className={styles.sortOptions}>
          <button
            className={`${styles.sortOption} ${sortBy === 'distance' ? styles.active : ''}`}
            onClick={() => handleSortChange('distance')}
          >
            <LocationOnIcon className={styles.sortIcon} />
            Distance
          </button>
          <button
            className={`${styles.sortOption} ${sortBy === 'rating' ? styles.active : ''}`}
            onClick={() => handleSortChange('rating')}
          >
            <StarIcon className={styles.sortIcon} />
            Rating
          </button>
          <button
            className={`${styles.sortOption} ${sortBy === 'cost' ? styles.active : ''}`}
            onClick={() => handleSortChange('cost')}
          >
            <SortIcon className={styles.sortIcon} />
            Cost
          </button>
          <button
            className={`${styles.sortOption} ${sortBy === 'availability' ? styles.active : ''}`}
            onClick={() => handleSortChange('availability')}
          >
            <AccessTimeIcon className={styles.sortIcon} />
            Soonest
          </button>
        </div>
      </div>

      <div className={styles.proceduresSection}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Related Procedures</h3>
          {specialtyInfo.procedures.length > 3 && (
            <button 
              className={styles.viewAllButton}
              onClick={handleViewAllProcedures}
            >
              View All
              <ChevronRightIcon className={styles.viewAllIcon} />
            </button>
          )}
        </div>
        <div className={styles.proceduresCarousel}>
          {specialtyInfo.procedures.slice(0, 4).map((procedure) => (
            <div key={procedure.cpt}>
              {renderProcedureCard(procedure)}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.providersSection}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Available Providers ({specialtyInfo.totalProviders})</h3>
          <button 
            className={styles.viewAllButton}
            onClick={handleViewAllProviders}
          >
            View All
            <ChevronRightIcon className={styles.viewAllIcon} />
          </button>
        </div>
        <div className={styles.providersCarousel}>
          {specialtyInfo.providers.slice(0, 3).map((provider) => (
            <div key={provider.npi}>
              {renderProviderCard(provider)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SpecialtyDetail;